import { Image, ScrollView, StyleSheet, Text, View } from 'react-native';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useIsDesktop } from '../../hooks/useIsDeskTop';
import { DinningLayout } from '../../WebLayout';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../services/redux/store';
import DinningPromotionItem from './components/DinningPromotionItem';
import { Button } from '../../components/Button';
import { STYLE_VARS } from '../../constants/style-vars';
import {
  PromotionFilter,
  clearPromotionFilter,
  setLoadingPromotion,
  setPromotionFilter,
  setPromotions,
  setTotal,
} from '../../services/redux/reducers/PromotionSlice';
import { PromotionsApi } from '../../services/api';
import { COLORS, FONTS } from '../../constants';
import { Loading } from '../../components';
import { Category } from '../../types/categories.types';
import PageTitle from '../../components/PageTitle';
import { TouchableOpacity } from 'react-native';
import { TextInput } from '../../components/TextInput/TextInput';
import Filter from '../../components/Filter';
import { IPromotionCategory } from '../../types/promotions.types';
import { Footer } from '../../components/Footer';
import { debounce } from 'lodash';

const DinningPromotion: FC = () => {
  const isDesktop = useIsDesktop();
  const dispatch = useDispatch();

  const { promotions, promotionFilter, total, isLoading } = useSelector(
    (state: RootState) => state.promotionReducer
  );

  const [toggleFilter, setToggleFilter] = useState<boolean>(false);
  const [filterSelected, setFilterSelected] = useState<Category[]>([]);

  const getPromotions = useCallback(
    async (props: PromotionFilter) => {
      try {
        dispatch(setLoadingPromotion(true));
        const res = await PromotionsApi.getPromotions(props);
        dispatch(setPromotions(res.data.promotions));
        dispatch(setTotal(res.data.total));
      } catch (error: any) {
        console.log(error);
      } finally {
        dispatch(setLoadingPromotion(false));
      }
    },
    [dispatch]
  );

  const handleLoadMore = useCallback(async () => {
    if (promotions.length < total) {
      dispatch(
        setPromotionFilter({
          ...promotionFilter,
          page: Math.ceil(promotions.length / 5) + 1,
        })
      );
      try {
        dispatch(setLoadingPromotion(true));
        const res = await PromotionsApi.getPromotions({
          ...promotionFilter,
          page: Math.ceil(promotions.length / 5) + 1,
        });
        dispatch(setPromotions([...promotions, ...res.data.promotions]));
        dispatch(setTotal(res.data.total));
      } catch (error: any) {
        console.log(error);
      } finally {
        dispatch(setLoadingPromotion(false));
      }
    }
  }, [promotions, total, dispatch, promotionFilter]);

  const [promotionCategories, setPromotionCategories] = useState<
    IPromotionCategory[]
  >([]);

  const getPromotionCategoriesData = useCallback(async () => {
    try {
      const res = await PromotionsApi.getPromotionCategories({});
      setPromotionCategories(res.data);
    } catch (error: any) {
      console.log(error);
    } finally {
    }
  }, []);

  const handleSearch = useCallback(
    (text: string) => {
      dispatch(setPromotions([]));
      getPromotions({
        ...promotionFilter,
        page: 1,
        search: text,
      });
      dispatch(
        setPromotionFilter({
          ...promotionFilter,
          page: 1,
          search: text,
        })
      );
    },
    [promotionFilter, dispatch, getPromotions]
  );

  const debouncedSearch = debounce(handleSearch, 500);

  useEffect(() => {
    getPromotionCategoriesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(clearPromotionFilter());
    getPromotions({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isDesktop()) {
    return (
      <DinningLayout
        title="Dining Promotions"
        icon={require('../../assets/dining-promotions.png')}
        current={2}
      >
        <View>
          {promotions.map((promotion) => (
            <DinningPromotionItem item={promotion} key={promotion.id} />
          ))}
        </View>
        {isLoading === false && promotions.length === 0 ? (
          <View style={styles.noDataWrapper}>
            <Text style={[styles.noDataText, { fontSize: 20 }]}>
              THERE ARE CURRENTLY NO PROMOTIONS
            </Text>
          </View>
        ) : (
          <></>
        )}
        {isLoading && <Loading style={{ paddingVertical: 30 }} />}
        {isLoading === false && total > promotions.length && (
          <View style={{ alignSelf: 'center', marginVertical: 30 }}>
            <Button
              text="LOAD MORE"
              style={STYLE_VARS.blueButton}
              onPress={handleLoadMore}
              disabled={isLoading}
            />
          </View>
        )}
      </DinningLayout>
    );
  }

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <View style={styles.container}>
        <PageTitle
          title="Dining Promotions"
          icon={require('../../assets/dining-promotions.png')}
          haveGoBackButton={true}
        />
      </View>
      <View style={[styles.container, { zIndex: 5 }]}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            marginVertical: 20,
          }}
        >
          <TouchableOpacity
            style={styles.navbarWrapper}
            onPress={() => setToggleFilter(!toggleFilter)}
          >
            <Text style={styles.textGreen}>Filter</Text>
            <Image
              source={require('../../assets/filter-button.png')}
              style={styles.navbarIconStyle}
            />
          </TouchableOpacity>
          <View style={styles.navbarWrapper}>
            <View style={{ width: 80 }}>
              <TextInput
                placeholder="SEARCH"
                placeholderTextColor={STYLE_VARS.primaryColor}
                onChangeText={debouncedSearch}
                style={{
                  fontSize: 16,
                  fontWeight: '500',
                  color: STYLE_VARS.primaryColor,
                  fontFamily: FONTS.IowanOldStyleBTRoman,
                  paddingHorizontal: 6,
                  paddingVertical: 1,
                  flex: 1,
                }}
              />
            </View>
            <Image
              source={require('../../assets/search_white.png')}
              style={styles.navbarIconStyle}
            />
          </View>
        </View>
        {toggleFilter && (
          <Filter
            categoriesData={promotionCategories}
            getData={getPromotions}
            filterData={promotionFilter}
            setFilterData={setPromotionFilter}
            setData={setPromotions}
            filterSelected={filterSelected}
            setFilterSelected={setFilterSelected}
          />
        )}
      </View>
      <View style={styles.container}>
        {promotions.map((promotion) => (
          <DinningPromotionItem item={promotion} key={promotion.id} />
        ))}
      </View>
      {isLoading === false && promotions.length === 0 ? (
        <View style={styles.noDataWrapper}>
          <Text style={[styles.noDataText, { fontSize: 16 }]}>
            THERE ARE CURRENTLY NO PROMOTIONS
          </Text>
        </View>
      ) : (
        <></>
      )}
      {isLoading && <Loading style={{ paddingVertical: 30 }} />}
      {isLoading === false && total > promotions.length && (
        <View style={{ paddingVertical: 40 }}>
          <Button
            style={STYLE_VARS.blueButton}
            text="VIEW MORE"
            onPress={handleLoadMore}
            disabled={isLoading}
          />
        </View>
      )}
      <Footer />
    </ScrollView>
  );
};

export default DinningPromotion;

const styles = StyleSheet.create({
  noDataWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  noDataText: {
    color: COLORS.GREEN,
    textTransform: 'uppercase',
    fontFamily: FONTS.AvenirMedium,
  },
  container: {
    paddingHorizontal: 20,
  },
  navbarWrapper: {
    width: '40%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  textGreen: {
    fontFamily: FONTS.IowanOldStyleBTRoman,
    fontSize: 16,
    fontWeight: '500',
    color: STYLE_VARS.textColor,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  navbarIconStyle: {
    width: 15,
    height: 15,
    resizeMode: 'contain',
    tintColor: STYLE_VARS.primaryColor,
    marginLeft: 5,
  },
});
